import React, {useEffect, useState} from "react";
import Modal from "../../../BrandSignup/Modal"
import {useMutation} from "@apollo/client";
import styles from "../../../TalentApplication/styles.module.css";
import { CHECK_NEW_PHONE_NUMBER_VERIFY_CODE, SEND_NEW_PHONE_NUMBER_VERIFY_CODE } from "../../WithUserData/queries";
import {parsePhoneNumberFromString} from "libphonenumber-js"
import {useForm} from "react-hook-form";
import PhoneNumberInput, { getCountryCallingCode } from "react-phone-number-input";
import {libphonenumberFilteredMetadata} from "../../../TalentApplication/StepDetails_1st/twilio-geo-permissions";

const UpdatePhoneNumberModal = ({ showPhoneNumberModal, setShowPhoneNumberModal, oldPhoneNumber, onSuccess, recaptchaRef }) => {

    const [sendNewPhoneNumberVerifyCode] = useMutation(SEND_NEW_PHONE_NUMBER_VERIFY_CODE)
    const [checkNewPhoneNumberVerifyCode] = useMutation(CHECK_NEW_PHONE_NUMBER_VERIFY_CODE)
    const [newNumber, setNewNumber] = useState(`${oldPhoneNumber}`);
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState();
    const [verifyError, setVerifyError] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    const {
        handleSubmit,
        formState: { errors },
        setError,
        clearErrors,
        reset,
    } = useForm()

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleNumberVerification = async (recaptchaToken) => {
        try {
            await sendNewNumberVerificationCode(recaptchaToken, newNumber);
            setCodeSent(true);
        } catch (err) {
            setError("newPhoneNumber", {
                type: "manual",
                message: err.message,
            });
        }
    }
    const sendNewNumberVerificationCode = async (recaptchaToken) => {
        setResendCooldown(30);
        await sendNewPhoneNumberVerifyCode({
            variables: {
                recaptchaToken,
                newPhoneNumber: newNumber
            }
        });
    }

    const closeModal = () => {
        reset()
        clearErrors();
        setCodeSent(false);
        setNewNumber(oldPhoneNumber)
        setShowPhoneNumberModal(false);
    }


    const handleCodeInput = async (val) => {
        const onlyNumbers = val.replace(/\D/g, "");
        setCode(onlyNumbers);

        if (onlyNumbers.length === 0) {
            setVerifyError(false);
            return;
        }

        if (onlyNumbers.length === 3) {
            try {
                let recaptchaToken = null;
                if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true') {
                    recaptchaToken = await recaptchaRef.current.executeAsync();
                    recaptchaRef.current.reset();
                }
                const { data } = await checkNewPhoneNumberVerifyCode({
                    variables: {
                        code: onlyNumbers,
                        recaptchaToken
                    }
                });

                if (data?.checkNewPhoneNumberVerifyCode) {
                    setShowPhoneNumberModal(false);
                    setVerifyError(false);
                    onSuccess();
                } else {
                    setVerifyError(true);
                }
            } catch {
                setVerifyError(true);
            }
        }
    };


    const customMetadata = libphonenumberFilteredMetadata();
    return (
        <Modal isOpenModal={showPhoneNumberModal} closeModal={closeModal} light={true}>
            {!codeSent && <form
                onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newNumber) {
                        setError("newPhoneNumber", { message: "Phone number is required" });
                        return;
                    }
                    const parsed = parsePhoneNumberFromString(newNumber);
                    if (!parsed?.isValid()) {
                        setError("newPhoneNumber", { message: "Invalid phone number" });
                        return;
                    } else if (newNumber === `${oldPhoneNumber}`) {
                        setError("newPhoneNumber", { message: "New number must be different from current number" });
                        return;
                    }
                    let token = null;
                    if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true') {
                        token = await recaptchaRef.current.executeAsync();
                        recaptchaRef.current.reset();
                    }
                    handleSubmit(() => handleNumberVerification(token))();
                }}
                className="space-y-4 w-full max-w-sm px-4 pb-6"
            >
                <div className="mb-3">
                    <label className="block mb-4 text-black">New phone number:</label>
                    <div className={`w-full bg-gray-300 hover:bg-gray-400 rounded border ${errors.newPhoneNumber ? "border-red-500" : "border-gray-900"}`}>
                        <PhoneNumberInput
                            placeholder="Enter number"
                            country="GB"
                            type="tel"
                            international={false}
                            displayInitialValueAsLocalNumber={true}
                            metadata={customMetadata}
                            className={`${styles.combobox_phone_number_update} `}
                            limitMaxLength={true}
                            value={newNumber}
                            onChange={(value) => {
                                if (value) {
                                    const parsed = parsePhoneNumberFromString(value);
                                    if (parsed?.isValid() ) {
                                        const localNumber = parsed.nationalNumber;
                                        const formatted = parsed.country ? `+${getCountryCallingCode(parsed.country)} ${localNumber}` : value;
                                        setNewNumber(formatted);
                                    } else {
                                        setNewNumber(value);
                                    }
                                } else {
                                    setNewNumber('');
                                }
                                clearErrors("newPhoneNumber");
                            }}
                            flagComponent={({ country }) => (
                                <span style={{ color: "black" }}>+{getCountryCallingCode(country)}</span>
                            )}
                        />
                    </div>
                    {errors.newPhoneNumber && <p className="text-red-500 text-sm">{errors.newPhoneNumber.message}</p>}
                </div>

                <div className="flex justify-content-end mt-16">
                    <button
                        type="button"
                        className="cursor-pointer w-24 py-1 bg-gray-300 text-black rounded-xl flex items-center justify-center mx-auto hover:bg-gray-400"
                        onClick={closeModal}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={`w-24 py-1 bg-gray-300 rounded-xl flex items-center justify-center mx-auto cursor-pointer text-black  hover:bg-gray-400`}
                    >
                        Verify
                    </button>
                </div>
            </form>}
            {newNumber && codeSent && <>
                <p className='text-black'>
                    We sent a text message to {newNumber} <br/>Please enter the verification code below:
                </p>

                <div className="mt-4">
                    <input
                        maxLength={3}
                        type="text"
                        inputMode="numeric"
                        value={code}
                        onChange={(e) => handleCodeInput(e.target.value)}
                        className="w-24 text-center text-black bg-gray-300 text-xl mt-2 p-2 rounded border bg-[#1A1A1A] border-white"
                    />
                    {verifyError && <p className="text-red-500 mt-2">Invalid code, please try again.</p>}
                </div>
                <div className="flex justify-content-end mt-8">
                    <button
                        onClick={async () => {
                            let token = null;
                            if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true') {
                                token = await recaptchaRef.current.executeAsync();
                                recaptchaRef.current.reset();
                            }
                            await sendNewNumberVerificationCode(token);
                        }}
                        disabled={resendCooldown > 0}
                        className={`px-3 py-1 rounded-xl flex items-center justify-center mx-auto ${resendCooldown > 0 ? 'bg-gray-200 cursor-not-allowed text-gray-500 ' : 'bg-gray-300 cursor-pointer text-black hover:bg-gray-400'} `}
                    >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown} seconds` : "Resend verification code"}
                    </button>
                </div>
            </>}
        </Modal>
    )
}

export default UpdatePhoneNumberModal;